import type { Store } from '@blocksuite/affine/store';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as Y from 'yjs';

import * as styles from './object-annotation.css';

/** Catalog survives detaching the last use; attached legacy metadata is included. */
export function annotationVocabulary(parent: Store, kind: 'labels' | 'tags') {
  const result = new Map<string, string>();
  const walk = (value: unknown) => {
    if (value instanceof Y.Map) {
      const annotation = value.get('mpxAnnotation');
      const data =
        annotation instanceof Y.Map ? annotation.toJSON() : annotation;
      if (data && typeof data === 'object') {
        const entries = (data as Record<string, Record<string, unknown>>)[kind];
        Object.entries(entries ?? {}).forEach(([name, color]) =>
          result.set(name, typeof color === 'string' ? color : '')
        );
      }
      value.forEach(walk);
    } else if (value instanceof Y.Array) value.forEach(walk);
  };
  parent.spaceDoc.share.forEach(walk);
  const catalog = parent.root?.yBlock.get('mpxAnnotationVocabulary');
  if (catalog instanceof Y.Map) {
    catalog.forEach((value, key) => {
      if (key.startsWith(kind + ':'))
        result.set(key.slice(kind.length + 1), String(value));
    });
  }
  return [...result.entries()].sort(([a], [b]) => a.localeCompare(b));
}

export function rememberVocabulary(
  parent: Store,
  kind: 'labels' | 'tags',
  name: string,
  color: string
) {
  const root = parent.root;
  if (!root || parent.readonly) return;
  let catalog = root.yBlock.get('mpxAnnotationVocabulary');
  if (!(catalog instanceof Y.Map)) {
    catalog = new Y.Map<string>();
    root.yBlock.set('mpxAnnotationVocabulary', catalog);
  }
  (catalog as Y.Map<string>).set(kind + ':' + name, color);
}

export function AnnotationPicker({
  parent,
  kind,
  attached,
  onSelect,
}: {
  parent: Store;
  kind: 'labels' | 'tags';
  attached: string[];
  onSelect: (name: string, color: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [color, setColor] = useState('#6c5ce7');
  const [vocabulary, setVocabulary] = useState<[string, string][]>([]);
  const holder = useRef<HTMLDivElement>(null);
  const popup = useRef<HTMLFormElement>(null);
  const singular = kind === 'labels' ? 'label' : 'tag';
  useEffect(() => {
    if (!open) return;
    const update = () => setVocabulary(annotationVocabulary(parent, kind));
    update();
    parent.spaceDoc.on('afterTransaction', update);
    const dismiss = (event: PointerEvent) => {
      if (
        !holder.current?.contains(event.target as Node) &&
        !popup.current?.contains(event.target as Node)
      )
        setOpen(false);
    };
    document.addEventListener('pointerdown', dismiss, true);
    return () => {
      parent.spaceDoc.off('afterTransaction', update);
      document.removeEventListener('pointerdown', dismiss, true);
    };
  }, [open, parent, kind]);
  const name = query.trim().replace(kind === 'tags' ? /^#/ : /^$/, '');
  const matches = vocabulary.filter(([value]) =>
    value.toLocaleLowerCase().includes(name.toLocaleLowerCase())
  );
  const choose = (value: string, selectedColor: string) => {
    onSelect(value, selectedColor || color);
    setQuery('');
    setOpen(false);
  };
  const anchor = holder.current?.getBoundingClientRect();
  const panel = holder.current?.closest('aside')?.getBoundingClientRect();
  const position =
    anchor && panel
      ? {
          left: Math.max(
            panel.left + 8,
            Math.min(anchor.left, panel.right - 288)
          ),
          top: Math.min(anchor.bottom + 6, window.innerHeight - 260),
        }
      : undefined;
  return (
    <div ref={holder} className={styles.pickerAnchor}>
      <button
        className={styles.addButton}
        aria-label={'Add ' + singular}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        +
      </button>
      {open &&
        createPortal(
          <form
            ref={popup}
            role="dialog"
            aria-label={'Select ' + singular}
            className={styles.picker}
            style={position}
            onKeyDown={event => {
              if (event.key === 'Escape') {
                event.preventDefault();
                setOpen(false);
                holder.current?.querySelector('button')?.focus();
              }
            }}
            onSubmit={event => {
              event.preventDefault();
              if (matches.length) {
                const first = matches.find(
                  ([value]) => !attached.includes(value)
                );
                if (first) choose(...first);
              } else if (name) choose(name, color);
            }}
          >
            <input
              autoFocus
              aria-label={singular === 'label' ? 'Label name' : 'Tag name'}
              placeholder={'Search ' + kind + '…'}
              maxLength={80}
              value={query}
              onChange={event => setQuery(event.target.value)}
            />
            <div className={styles.pickerResults}>
              {matches.map(([value, valueColor]) => (
                <button
                  type="button"
                  className={styles.pickerOption}
                  key={value}
                  disabled={attached.includes(value)}
                  onClick={() => choose(value, valueColor)}
                >
                  {kind === 'labels' && (
                    <span
                      className={styles.colorDot}
                      style={{ background: valueColor }}
                    />
                  )}
                  <span>{value}</span>
                  {attached.includes(value) && <small>Added ✓</small>}
                </button>
              ))}
              {!matches.length &&
                (name ? (
                  <>
                    {kind === 'labels' && (
                      <div className={styles.colorRow}>
                        {[
                          '#6c5ce7',
                          '#2563eb',
                          '#059669',
                          '#d97706',
                          '#dc2626',
                          '#db2777',
                          '#64748b',
                        ].map(value => (
                          <button
                            type="button"
                            key={value}
                            aria-label={'Label color ' + value}
                            aria-pressed={value === color}
                            style={{ backgroundColor: value }}
                            onClick={() => setColor(value)}
                          />
                        ))}
                      </div>
                    )}
                    <button type="submit" className={styles.pickerOption}>
                      Create new {singular} “{name}”
                    </button>
                  </>
                ) : (
                  <span className={styles.kind}>
                    Type to create a {singular}
                  </span>
                ))}
            </div>
          </form>,
          document.body
        )}
    </div>
  );
}
